import { FC, useEffect, useState } from 'react'
import { ApolloError, useMutation } from '@apollo/client'
import { PageLoader } from 'components/PageLoader'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { CreateDentalSchoolInput } from '__generatedTypes__/globalTypes'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { createDentalSchool, createDentalSchoolVariables } from '__generatedTypes__/createDentalSchool'
import { CREATE_DENTAL_SCHOOL } from 'queries/dentalSchools'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { toast } from 'utils/toast'
import { FormValues, Form, getInitialValues } from '../Form'

type Props = {
  platform: Platform
  isOptometry?: boolean
}

export const REFETCH_DENTAL_SCHOOLS_MESSAGE = 'refetch-dental-schools'
const BAD_SLUG_DENTAL_SCHOOL_ERROR_MESSAGE = 'Dental School with this slug already created :('

export const handleServerErrors = (error: unknown) => {
  if (error && typeof error === 'object' && 'graphQLErrors' in error) {
    const existingSlugErrorIndex = (error.graphQLErrors as ApolloError['graphQLErrors']).findIndex(({ message }) =>
      [BAD_SLUG_DENTAL_SCHOOL_ERROR_MESSAGE].includes(message)
    )

    if (existingSlugErrorIndex !== -1) {
      return { slug: 'School with this slug already exists' }
    }
  }
}

export const Create: FC<Omit<Props, 'platform'>> = withPlatformCheck(({ platform, isOptometry }: Props) => {
  const bc = useBroadcastChannel()
  const [initialValues, setInitialValues] = useState<FormValues | null>(null)
  const [createDentalSchool] = useMutation<createDentalSchool, createDentalSchoolVariables>(CREATE_DENTAL_SCHOOL)

  useEffect(() => {
    ;(async () => {
      const initialValues = await getInitialValues(platform.id)
      setInitialValues(initialValues)
    })()
  }, [platform.id])

  const handleSubmit = async (values: CreateDentalSchoolInput) => {
    try {
      await createDentalSchool({
        variables: {
          createDentalSchoolInput: {
            ...values,
            isOptometry: !!isOptometry,
          },
        },
      })

      bc.postMessage(REFETCH_DENTAL_SCHOOLS_MESSAGE)
      window.close()
    } catch (error) {
      toast.error(`Failed to create ${isOptometry ? 'optometry' : 'dental'} school`)

      return handleServerErrors(error)
    }
  }

  if (!initialValues) {
    return <PageLoader />
  }

  return <Form initialValues={initialValues} isOptometry={!!isOptometry} onSubmit={handleSubmit} />
})
